/**
 * Dépliant de détail d'un POI — EF-RBK-002, EF-RBK-011, EF-POI-011.
 *
 * Ce n'est pas un écran : le contenu s'ouvre sous la ligne, la liste n'est
 * jamais quittée et sa position est conservée par construction. La curation
 * n'est accessible que d'ici ; la ligne ne porte aucune commande.
 *
 * Le téléphone et le site web sont affichés **en clair** sous leur libellé :
 * un numéro doit pouvoir être lu ou dicté sans être composé. Les coordonnées
 * géographiques, elles, ne sont jamais affichées — le lien « Afficher sur une
 * carte » les transmet au système.
 */

import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { categoryFamily, categoryLabel } from '../../core/categories';
import { nominalCategory, type Poi, type PoiId, type RoadbookDocument } from '../../core/model';
import { LIMITS } from '../../core/parameters';
import { CategoryIcon, Glyph, PinRing } from '../icons';
import { formatArrival, formatDistance } from '../format';
import { ACTIONS, MARKERS, PRESENCE } from '../labels';
import {
  closeSheet,
  deleteManualPoi,
  editingPoiId,
  openSheet,
  pendingConfirmation,
  readOnly,
  setExcluded,
  setNote,
  setPinned,
} from '../state';

const FAMILY_CLASS: Readonly<Record<string, string>> = {
  water: 'family-water',
  supply: 'family-supply',
  rest: 'family-rest',
  race: 'family-race',
};

export interface PoiDetailProps {
  readonly poi: Poi;
  readonly document: RoadbookDocument;
}

export function PoiDetail(props: PoiDetailProps): JSX.Element {
  const { poi, document } = props;
  const id: PoiId = poi.id;
  const [noteDraft, setNoteDraft] = useState<string | null>(null);
  const [showAllHours, setShowAllHours] = useState(false);

  const track = document.route.track;
  const totals = document.results.totals;
  const category = nominalCategory(poi);
  const family = FAMILY_CLASS[categoryFamily(category)] ?? '';
  const icon = <CategoryIcon category={category} pinned={poi.userState.pinned} decorative />;
  const note = noteDraft ?? poi.userState.note ?? '';
  const noteLength = [...note.normalize('NFC')].length;
  const name = poi.name ?? categoryLabel(category);

  const webUsable = poi.website !== null && /^https?:\/\//i.test(poi.website);
  const phoneUsable = poi.phone !== null && /[0-9]/.test(poi.phone);

  return (
    <div class="poi-detail">
      <div class="poi-detail__head">
        <span class={family}>{poi.userState.pinned ? <PinRing>{icon}</PinRing> : icon}</span>
        <div>
          <h3>{name}</h3>
          <p class="muted">{poi.categories.map((key) => categoryLabel(key)).join(', ')}</p>
        </div>
      </div>

      <dl class="definition-list">
        {poi.openingHours !== null ? (
          <div>
            <dt>Horaires d’ouverture</dt>
            <dd>
              <span class={showAllHours ? undefined : 'clamp-4'}>{poi.openingHours}</span>
              <button
                type="button"
                class="link-button"
                onClick={() => {
                  setShowAllHours(!showAllHours);
                }}
              >
                {showAllHours ? 'Afficher moins' : 'Afficher plus'}
              </button>
            </dd>
          </div>
        ) : null}
        {poi.userState.presenceState !== 'present' ? (
          <div>
            <dt>État dans le dernier résultat</dt>
            <dd>
              {PRESENCE[poi.userState.presenceState]}
              {poi.userState.rejectionCause !== null ? ` (${poi.userState.rejectionCause})` : ''}
            </dd>
          </div>
        ) : null}
        {poi.accessNote === 'customers' ? (
          <div>
            <dt>Accès</dt>
            <dd>{MARKERS.customersOnly}</dd>
          </div>
        ) : null}
        {poi.userState.outOfSearchedPortion ? (
          <div>
            <dt>Portion</dt>
            <dd>{MARKERS.outOfSearchedPortion}</dd>
          </div>
        ) : null}
        {poi.userState.categoriesChanged ? (
          <div>
            <dt>Catégories</dt>
            <dd>{MARKERS.categoriesChanged}</dd>
          </div>
        ) : null}
      </dl>

      {/* Affichés en clair, et non réduits à un bouton d'action. */}
      {poi.phone !== null || poi.email !== null || poi.website !== null ? (
        <div class="poi-detail__contacts">
          {poi.phone !== null ? (
            <p class="contact">
              <span class="contact__label">Téléphone</span>
              {phoneUsable ? (
                <a class="contact__value" href={`tel:${poi.phone}`}>
                  {poi.phone}
                </a>
              ) : (
                <span class="contact__value">{poi.phone}</span>
              )}
            </p>
          ) : null}
          {poi.email !== null ? (
            <p class="contact">
              <span class="contact__label">Adresse électronique</span>
              <a class="contact__value" href={`mailto:${poi.email}`}>
                {poi.email}
              </a>
            </p>
          ) : null}
          {poi.website !== null ? (
            <p class="contact">
              <span class="contact__label">Site web</span>
              {webUsable ? (
                <a class="contact__value" href={poi.website} target="_blank" rel="noreferrer noopener">
                  {poi.website}
                </a>
              ) : (
                <span class="contact__value">{poi.website}</span>
              )}
            </p>
          ) : null}
        </div>
      ) : null}

      <label class="poi-detail__note">
        <span class="field-label">
          Commentaire ({noteLength} / {LIMITS.noteCodePoints} points de code)
        </span>
        <textarea
          value={note}
          disabled={readOnly.value}
          placeholder="Ce que vous voulez retrouver ici à 3 h du matin."
          onInput={(event) => {
            const value = (event.currentTarget as HTMLTextAreaElement).value;
            // La saisie refuse la frappe au-delà de la limite : aucune
            // troncature silencieuse n'est autorisée.
            if ([...value.normalize('NFC')].length <= LIMITS.noteCodePoints) setNoteDraft(value);
          }}
          onBlur={() => {
            if (noteDraft !== null) setNote(id, noteDraft);
            setNoteDraft(null);
          }}
        />
      </label>

      <div class="actions">
        {/* Délégation au système ; ce n'est pas un affichage cartographique. */}
        <a
          class="button"
          aria-label={ACTIONS.openInMaps}
          href={`geo:${poi.coordinate.lat},${poi.coordinate.lon}?q=${poi.coordinate.lat},${poi.coordinate.lon}(${encodeURIComponent(name)})`}
        >
          <Glyph name="carte" label="" size={20} decorative />
        </a>
        <button
          type="button"
          disabled={readOnly.value}
          aria-pressed={poi.userState.pinned}
          onClick={() => {
            setPinned(id, !poi.userState.pinned);
          }}
        >
          {poi.userState.pinned ? ACTIONS.unpin : ACTIONS.pin}
        </button>
        <button
          type="button"
          disabled={readOnly.value}
          aria-pressed={poi.userState.excluded}
          onClick={() => {
            setExcluded(id, !poi.userState.excluded);
          }}
        >
          {poi.userState.excluded ? ACTIONS.restore : ACTIONS.exclude}
        </button>
      </div>

      {poi.origin === 'manual' ? (
        <div class="actions">
          <button
            type="button"
            disabled={readOnly.value}
            onClick={() => {
              editingPoiId.value = id;
              openSheet('manualPoi');
            }}
          >
            Modifier ce POI
          </button>
          <button
            type="button"
            class="danger"
            disabled={readOnly.value}
            onClick={() => {
              pendingConfirmation.value = {
                title: 'Supprimer définitivement ce POI ?',
                body: `« ${name} » sera réellement retiré du document. Cette suppression n’est pas annulable.`,
                confirmLabel: 'Supprimer définitivement',
                offerExport: false,
                onConfirm: () => {
                  closeSheet();
                  deleteManualPoi(id);
                },
              };
            }}
          >
            {ACTIONS.deleteManualPoi}
          </button>
        </div>
      ) : null}

      <p class="visually-hidden">
        Longueur du parcours : {formatDistance(track.lengthMeters)}
        {totals === null ? '' : `, départ ${formatArrival(totals.startLocal, null)}`}
      </p>
    </div>
  );
}
